/**
 * Lightweight PMV equipment label map for registers that only need profile display names.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import pmvService from '@/services/pmv.service';
import { formatHireEquipmentLabel } from '@/utils/pmvHireUi';

export function usePmvEquipmentLabels(opts?: { enabled?: boolean; pageSize?: number }) {
  const [search, setSearch] = useState('');
  const enabled = opts?.enabled !== false;

  const q = useQuery({
    queryKey: ['pmv-equipment-labels', search, opts?.pageSize ?? 200],
    queryFn: () =>
      pmvService.listProfiles({
        page: 1,
        pageSize: opts?.pageSize ?? 200,
        q: search || undefined,
      }),
    enabled,
    staleTime: 60_000,
  });

  const labelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of q.data?.data ?? []) {
      m.set(p.id, formatHireEquipmentLabel(p));
    }
    return m;
  }, [q.data]);

  const options = useMemo(
    () =>
      (q.data?.data ?? []).map((p) => ({
        value: p.id,
        label: formatHireEquipmentLabel(p),
      })),
    [q.data]
  );

  return {
    labelById,
    options,
    search,
    setSearch,
    isLoading: q.isLoading,
    resolve: (id: string | null | undefined, fallback = '—') => {
      if (!id) return fallback;
      return labelById.get(id) || fallback;
    },
  };
}
